import { useStore } from 'vuex'
import { computed, getCurrentInstance } from 'vue'
import rootStore from '@/store'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import usePrompts from '@/composables/usePrompts'
import {
  DataType,
  FieldType,
  SchemaType,
  MAX_FIELD_DESCRIPTION_LENGTH,
  MAX_DESCRIPTION_LENGTH
} from '@/constants/schema'
import { runAiGeneration } from '@/utils/helpHero'

export const optimisticSchemaUpdate = async (workflow, updatedSchema) => {
  const existingSchemas = workflow.schemas || []
  const schemaIndex = existingSchemas.findIndex((s) => s.id === updatedSchema.id)
  if (schemaIndex === -1) {
    return
  }

  const previousSchema = existingSchemas[schemaIndex]
  const updatedSchemas = existingSchemas.map((schema, index) => (
    index === schemaIndex ? updatedSchema : schema
  ))

  const snapshotWorkflow = { ...workflow, schemas: existingSchemas }

  rootStore.commit('workflow/updateWorkflow', {
    id: workflow.id,
    schemas: [updatedSchema]
  })

  try {
    await workflowApi.updateSchemas({
      schemas: updatedSchemas,
      workflow: snapshotWorkflow
    })
  } catch (error) {
    rootStore.commit('workflow/updateWorkflow', {
      id: workflow.id,
      schemas: [previousSchema]
    })
  }
}

const useReadModelActions = () => {
  const store = useStore()
  const { proxy } = getCurrentInstance()
  const { getFieldExampleData } = usePrompts()
  const workflow = computed(() => store.state.workflow.workflow)

  const getFieldAtPath = (fields, path) => {
    let current = { fields }
    for (const index of path) {
      if (!current.fields || index < 0 || index >= current.fields.length) {
        return null
      }
      current = current.fields[index]
    }
    return current
  }

  const moveRight = async (schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    if (newSchemas[schemaIndex].fields.length > (fieldIndex + 1)) {
      const field = newSchemas[schemaIndex].fields[fieldIndex]

      newSchemas[schemaIndex].fields[fieldIndex] = newSchemas[schemaIndex].fields[fieldIndex + 1]
      newSchemas[schemaIndex].fields[fieldIndex + 1] = field
      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: workflow.value
      })
    }
  }

  const moveLeft = async (schemaId, fieldIndex) => {
    if (fieldIndex >= 1) {
      const newSchemas = safeStructuredClone(workflow.value.schemas || [])
      const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
      if (schemaIndex === -1) {
        return
      }

      const field = newSchemas[schemaIndex].fields[fieldIndex]
      newSchemas[schemaIndex].fields[fieldIndex] = newSchemas[schemaIndex].fields[fieldIndex - 1]
      newSchemas[schemaIndex].fields[fieldIndex - 1] = field

      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: workflow.value
      })
    }
  }

  const blurReadModelName = async (e, schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const newName = e.target.innerText.trim()
    if (newSchemas[schemaIndex].name === newName) {
      return
    }

    const currentReadModel = newSchemas[schemaIndex]
    const hasBoundedContext = !!currentReadModel.boundedContext
    const isDuplicate = hasBoundedContext && newSchemas.some((s) =>
      s.id !== schemaId &&
      s.type === SchemaType.QUERY &&
      s.boundedContext === currentReadModel.boundedContext &&
      s.name.toLowerCase() === newName.toLowerCase()
    )

    if (isDuplicate) {
      e.target.innerText = currentReadModel.name
      const bcName = (workflow.value.boundedContexts || []).find((bc) => bc.id === currentReadModel.boundedContext)?.name || 'this bounded context'
      proxy.$store.commit('snackbar/showMessage', {
        content: `A read model with the name "${newName}" already exists in ${bcName}`,
        timeout: 4000,
        color: 'red'
      }, { root: true })
      return
    }

    newSchemas[schemaIndex].name = newName
    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const blurReadModelDescription = async (e, schemaId) => {
    const schema = workflow.value.schemas?.find((s) => s.id === schemaId)
    if (!schema) {
      return
    }

    const newDescription = (e.target.innerText || '').trim()
    const currentDescription = schema.description || ''

    if (newDescription.length > MAX_DESCRIPTION_LENGTH) {
      return
    }

    if (newDescription === currentDescription) {
      return
    }

    await optimisticSchemaUpdate(workflow.value, { ...schema, description: newDescription || null })
  }

  const blurFieldDescription = async (e, schemaId, fieldIndex, subFieldIndex = -1) => {
    const schema = workflow.value.schemas?.find((s) => s.id === schemaId)
    if (!schema) {
      return
    }

    const parentField = schema.fields?.[fieldIndex]
    const targetField = subFieldIndex >= 0
      ? parentField?.fields?.[subFieldIndex]
      : parentField
    if (!targetField) {
      return
    }

    const newDescription = (e.target.innerText || '').trim()
    const currentDescription = targetField.description || ''

    if (newDescription.length > MAX_FIELD_DESCRIPTION_LENGTH) {
      return
    }

    if (newDescription === currentDescription) {
      return
    }

    let fields
    if (subFieldIndex >= 0) {
      const updatedSubField = { ...targetField, description: newDescription || null }
      const updatedSubFields = parentField.fields.map((f, index) => (
        index === subFieldIndex ? updatedSubField : f
      ))
      const updatedField = { ...parentField, fields: updatedSubFields }
      fields = schema.fields.map((f, index) => (index === fieldIndex ? updatedField : f))
    } else {
      const updatedField = { ...parentField, description: newDescription || null }
      fields = schema.fields.map((f, index) => (index === fieldIndex ? updatedField : f))
    }

    await optimisticSchemaUpdate(workflow.value, { ...schema, fields })
  }

  const blurTableCell = async (e, schemaId, fieldIndex, fieldName, indexAttribute = -1) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    let oldVal
    const newVal = e.target.innerText?.trim() ?? ''
    if (indexAttribute !== null && indexAttribute >= 0) {
      oldVal = workflow.value.schemas[schemaIndex].fields[fieldIndex][fieldName][indexAttribute]
    } else {
      oldVal = workflow.value.schemas[schemaIndex].fields[fieldIndex][fieldName]
    }
    if (oldVal === undefined && newVal === '') {
      return
    }
    if (oldVal !== newVal) {
      if (indexAttribute !== null && indexAttribute >= 0) {
        newSchemas[schemaIndex].fields[fieldIndex][fieldName][indexAttribute] = newVal
      } else {
        newSchemas[schemaIndex].fields[fieldIndex][fieldName] = newVal
      }
      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: workflow.value
      })
    }
  }

  const addColumn = async (schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].fields.push({
      name: 'NEW FIELD ' + (newSchemas[schemaIndex].fields.length + 1),
      cardinality: 'one-to-one',
      exampleData: [''],
      dataType: DataType.STRING,
      relatedEntityId: null
    })
    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const deleteReadModel = async (schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas.splice(schemaIndex, 1)

    // Unlink any cards that reference this schema
    const svgGraph = store.getters['svgGraphData/svgGraphData']
    const updatedSvgGraph = safeStructuredClone(svgGraph)
    let svgGraphChanged = false

    Object.keys(updatedSvgGraph).forEach((eventId) => {
      const eventData = updatedSvgGraph[eventId]
      if (eventData.cards) {
        eventData.cards = eventData.cards.map((card) => {
          if (card.schemaId === schemaId) {
            svgGraphChanged = true
            return { ...card, schemaId: null }
          }
          return card
        })
      }
    })

    await workflowApi.updateWorkflow({
      svgGraph: svgGraphChanged ? updatedSvgGraph : undefined,
      schemas: newSchemas,
      workflow: workflow.value,
      updateNote: {
        action: 'deleted read model',
        target: ''
      }
    }, true)
  }

  const setDataType = async (schemaId, fieldIndex, dataType, relatedEntityId = null) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }
    const field = newSchemas[schemaIndex].fields[fieldIndex]

    field.dataType = dataType
    field.relatedEntityId = relatedEntityId

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  // Its saves are the AI's, not the person's own edits (activation tracking).
  const generateExampleData = (schemaId, fieldIndex) => runAiGeneration(() => writeExampleData(schemaId, fieldIndex))

  const writeExampleData = async (schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const readModel = newSchemas[schemaIndex]

    if (!readModel || !readModel.fields) {
      return
    }

    proxy.$loadingState.start()

    try {
      const field = readModel.fields[fieldIndex]

      const response = await getFieldExampleData(readModel, field)

      if (!response?.exampleData) {
        proxy.$loadingState.stop()
        return
      }

      newSchemas[schemaIndex].fields[fieldIndex].exampleData = response.exampleData

      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: workflow.value
      })
    } catch (error) {
      if (error.name !== 'AbortError') {
        await store.dispatch('workflow/openRetryDialog', {
          failedItems: [{ type: 'exampleData', name: 'Generate Example Data' }],
          retryCallback: () => generateExampleData(schemaId, fieldIndex)
        })
      }
    } finally {
      proxy.$loadingState.stop()
    }
  }

  const deleteExampleDataRow = async (schemaId, rowIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const readModel = newSchemas[schemaIndex]

    if (!readModel || !readModel.fields) {
      return
    }

    readModel.fields.forEach((field) => {
      if (
        field.exampleData &&
        Array.isArray(field.exampleData) &&
        rowIndex >= 0 &&
        rowIndex < field.exampleData.length
      ) {
        field.exampleData.splice(rowIndex, 1)
      }
    })

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const setReadModelCardinality = async (schemaId, cardinality) => {
    const schema = workflow.value.schemas?.find((s) => s.id === schemaId)
    if (!schema || schema.cardinality === cardinality) {
      return
    }

    await optimisticSchemaUpdate(workflow.value, { ...schema, cardinality })
  }

  const toggleFieldComputed = async (schemaId, fieldIndex, subFieldIndex = -1) => {
    const schema = workflow.value.schemas?.find((s) => s.id === schemaId)
    if (!schema) {
      return
    }

    if (subFieldIndex >= 0) {
      const parentField = schema.fields?.[fieldIndex]
      const subField = parentField?.fields?.[subFieldIndex]
      if (!subField) {
        return
      }
      const updatedSubField = { ...subField, computed: !subField.computed }
      const updatedSubFields = parentField.fields.map((f, index) => (
        index === subFieldIndex ? updatedSubField : f
      ))
      const updatedField = { ...parentField, fields: updatedSubFields }
      const fields = schema.fields.map((f, index) => (index === fieldIndex ? updatedField : f))

      await optimisticSchemaUpdate(workflow.value, { ...schema, fields })

      return
    }

    const field = schema.fields?.[fieldIndex]
    if (!field) {
      return
    }

    const updatedField = { ...field, computed: !field.computed }
    const fields = schema.fields.map((f, index) => (index === fieldIndex ? updatedField : f))

    await optimisticSchemaUpdate(workflow.value, { ...schema, fields })
  }

  const toggleFieldFilter = async (schemaId, fieldIndex, subFieldIndex = -1) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) return

    let field
    if (subFieldIndex >= 0) {
      field = newSchemas[schemaIndex].fields[fieldIndex]?.fields?.[subFieldIndex]
    } else {
      field = newSchemas[schemaIndex].fields[fieldIndex]
    }
    if (!field) return

    field.type = field.type === FieldType.FILTER ? FieldType.NORMAL : FieldType.FILTER
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const blurFieldDescriptionAtPath = async (e, schemaId, path) => {
    const schema = workflow.value.schemas?.find((s) => s.id === schemaId)
    if (!schema || !path.length) {
      return
    }

    const clonedSchema = safeStructuredClone(schema)
    const targetField = getFieldAtPath(clonedSchema.fields, path)
    if (!targetField) {
      return
    }

    const newDescription = (e.target.innerText || '').trim()
    const currentDescription = targetField.description || ''

    if (newDescription.length > MAX_FIELD_DESCRIPTION_LENGTH) {
      return
    }

    if (newDescription === currentDescription) {
      return
    }

    targetField.description = newDescription || null

    await optimisticSchemaUpdate(workflow.value, clonedSchema)
  }

  const toggleFieldComputedAtPath = async (schemaId, path) => {
    const schema = workflow.value.schemas?.find((s) => s.id === schemaId)
    if (!schema || !path.length) {
      return
    }

    const clonedSchema = safeStructuredClone(schema)
    const field = getFieldAtPath(clonedSchema.fields, path)
    if (!field) {
      return
    }

    field.computed = !field.computed

    await optimisticSchemaUpdate(workflow.value, clonedSchema)
  }

  const toggleFieldFilterAtPath = async (schemaId, path) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1 || !path.length) {
      return
    }

    const field = getFieldAtPath(newSchemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    field.type = field.type === FieldType.FILTER ? FieldType.NORMAL : FieldType.FILTER
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const addDomainEventLink = async (schemaId, domainEventId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) return

    const existing = newSchemas[schemaIndex].domainEventIds || []
    if (existing.includes(domainEventId)) return

    newSchemas[schemaIndex].domainEventIds = [...existing, domainEventId]
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const removeDomainEventLink = async (schemaId, domainEventId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) return

    const existing = newSchemas[schemaIndex].domainEventIds || []
    newSchemas[schemaIndex].domainEventIds = existing.filter((id) => id !== domainEventId)
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  return {
    moveRight,
    moveLeft,
    blurReadModelName,
    blurReadModelDescription,
    blurFieldDescription,
    blurTableCell,
    addColumn,
    deleteReadModel,
    setDataType,
    generateExampleData,
    deleteExampleDataRow,
    setReadModelCardinality,
    toggleFieldFilter,
    toggleFieldComputed,
    blurFieldDescriptionAtPath,
    toggleFieldComputedAtPath,
    toggleFieldFilterAtPath,
    addDomainEventLink,
    removeDomainEventLink
  }
}

export default useReadModelActions
